import {
  ArrowRight,
  Building2,
  CalendarDays,
  Check,
  Mail,
  MapPin,
  Phone
} from 'lucide-react';
import { ProfileData } from './hooks/useProfileData';
import { FeaturedGoogleReviews } from './components/FeaturedGoogleReviews';
import { PublicProfileLeadForm } from './components/PublicProfileLeadForm';
import { PublicProfileSocialLinks } from './components/PublicProfileSocialLinks';
import { PublicProfileComplianceFooter } from './components/PublicProfileComplianceFooter';
import { PublicProfileScrollButton } from './components/PublicProfileScrollButton';
import { deriveYearsOfExperience } from '@/components/users/userProfileExperience';

interface AppProps {
  profileData?: ProfileData;
}

function fullName(profile: ProfileData): string {
  return `${profile.firstName} ${profile.lastName}`.trim() || 'Loan Officer';
}

export default function App({ profileData }: AppProps) {
  if (!profileData) {
    return null;
  }

  const profile = profileData;
  const derivedExperience = deriveYearsOfExperience(profile.industryStartYear);
  const displayedExperience = derivedExperience ?? profile.yearsOfExperience;
  const displayedStartYear = derivedExperience === null
    ? undefined
    : profile.industryStartYear;
  const name = fullName(profile);
  const portrait = profile.bodyPhoto || profile.facialPhoto;
  const hasSocialLinks = Object.values(profile.socialMedia).some(Boolean);

  return (
    <div className="min-h-screen overflow-x-hidden bg-[#f7f5f0] text-slate-950">
      <header className="border-b border-slate-900/10 bg-[#f7f5f0]/95 backdrop-blur">
        <div className="mx-auto flex min-h-16 max-w-7xl items-center justify-between px-5 sm:px-8">
          <PublicProfileScrollButton targetId="top" className="min-h-11 text-left text-lg font-semibold tracking-[-0.02em] sm:text-xl">
            {profile.companyInfo.name}
          </PublicProfileScrollButton>
          <div className="flex items-center gap-3">
            {profile.nmls && <span className="hidden text-xs text-slate-600 sm:inline">NMLS #{profile.nmls}</span>}
            <PublicProfileScrollButton
              targetId="contact"
              className="inline-flex min-h-11 items-center rounded-full bg-slate-950 px-5 text-sm font-semibold text-white transition hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-950 focus:ring-offset-2"
            >
              Let’s talk
            </PublicProfileScrollButton>
          </div>
        </div>
      </header>

      <main id="top">
        <section className="relative border-b border-slate-900/10">
          <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 py-16 sm:px-8 sm:py-24 lg:grid-cols-[1.05fr_0.95fr] lg:py-28">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.24em] text-primary">
                Personal mortgage guidance
              </p>
              <h1 className="mt-6 max-w-3xl text-5xl font-semibold leading-[0.98] tracking-[-0.04em] sm:text-6xl lg:text-7xl">
                Clear decisions start with a real conversation.
              </h1>
              <p className="mt-8 text-xl font-medium text-slate-900">{name}</p>
              <p className="mt-1 text-base text-slate-600">{profile.title}</p>

              <div className="mt-9 flex flex-col gap-3 sm:flex-row">
                {profile.applicationLink && (
                  <a
                    href={profile.applicationLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex min-h-12 items-center justify-center gap-2 rounded-full bg-primary px-6 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                  >
                    Start an application <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </a>
                )}
                <PublicProfileScrollButton
                  targetId="contact"
                  className="inline-flex min-h-12 items-center justify-center rounded-full border border-slate-300 px-6 text-sm font-semibold transition hover:bg-white focus:outline-none focus:ring-2 focus:ring-slate-950 focus:ring-offset-2"
                >
                  Ask a question
                </PublicProfileScrollButton>
              </div>

              <dl className="mt-12 grid gap-4 border-t border-slate-900/10 pt-8 sm:grid-cols-2">
                {(displayedStartYear !== undefined || displayedExperience !== undefined) && (
                  <div>
                    <dt className="text-xs uppercase tracking-wider text-slate-500">Mortgage experience</dt>
                    <dd className="mt-1 font-semibold">
                      {displayedStartYear !== undefined
                        ? `Since ${displayedStartYear}`
                        : `${displayedExperience} years`}
                    </dd>
                  </div>
                )}
                {profile.licenseStates.length > 0 && (
                  <div>
                    <dt className="text-xs uppercase tracking-wider text-slate-500">Licensed in</dt>
                    <dd className="mt-1 font-semibold">{profile.licenseStates.join(', ')}</dd>
                  </div>
                )}
              </dl>
            </div>

            <div className="relative mx-auto w-full max-w-xl">
              <div className="absolute -inset-4 rotate-2 rounded-[2rem] bg-primary/10" aria-hidden="true" />
              <div className="relative aspect-[4/5] overflow-hidden rounded-[2rem] bg-slate-200 shadow-2xl">
                {portrait ? (
                  <img src={portrait} alt={name} className="h-full w-full object-cover object-top" />
                ) : (
                  <div className="flex h-full items-center justify-center bg-slate-900 text-7xl font-semibold text-white">
                    {profile.firstName.charAt(0)}{profile.lastName.charAt(0)}
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {(profile.bio || profile.location || hasSocialLinks) && (
          <section className="bg-white py-16 sm:py-24" aria-labelledby="professional-about">
            <div className="mx-auto grid max-w-6xl gap-12 px-5 sm:px-8 lg:grid-cols-[0.7fr_1.3fr]">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Your loan officer</p>
                <h2 id="professional-about" className="mt-3 text-3xl font-semibold tracking-tight">Meet {profile.firstName}</h2>
                <div className="mt-7 space-y-4 text-sm text-slate-600">
                  {profile.location && (
                    <p className="flex items-start gap-3"><MapPin className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />{profile.location}</p>
                  )}
                  {profile.phone && (
                    <a className="flex min-h-11 items-center gap-3 hover:text-primary" href={`tel:${profile.phone}`}>
                      <Phone className="h-4 w-4" aria-hidden="true" />{profile.phone}
                    </a>
                  )}
                  {profile.email && (
                    <a className="flex min-h-11 items-center gap-3 break-all hover:text-primary" href={`mailto:${profile.email}`}>
                      <Mail className="h-4 w-4" aria-hidden="true" />{profile.email}
                    </a>
                  )}
                  {profile.calendarLink && (
                    <a className="flex min-h-11 items-center gap-3 hover:text-primary" href={profile.calendarLink} target="_blank" rel="noopener noreferrer">
                      <CalendarDays className="h-4 w-4" aria-hidden="true" />Schedule a conversation
                    </a>
                  )}
                </div>
                <PublicProfileSocialLinks
                  links={profile.socialMedia}
                  className="mt-6"
                  linkClassName="border-slate-200 hover:border-primary hover:text-primary"
                />
              </div>
              {profile.bio && <p className="whitespace-pre-line text-lg leading-8 text-slate-700 sm:text-xl sm:leading-9">{profile.bio}</p>}
            </div>
          </section>
        )}

        {profile.mortgageSpecialties.length > 0 && (
          <section className="py-16 sm:py-24" aria-labelledby="professional-programs">
            <div className="mx-auto max-w-6xl px-5 sm:px-8">
              <div className="max-w-2xl">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Financing options</p>
                <h2 id="professional-programs" className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">Loan programs I work with</h2>
              </div>
              <ul className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {profile.mortgageSpecialties.map((specialty) => (
                  <li key={specialty} className="flex items-center gap-3 rounded-xl border border-slate-900/10 bg-white p-4 text-sm font-medium shadow-sm">
                    <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary"><Check className="h-4 w-4" aria-hidden="true" /></span>
                    {specialty}
                  </li>
                ))}
              </ul>
            </div>
          </section>
        )}

        <FeaturedGoogleReviews reviews={profile.reviews} reviewsUrl={profile.reviewsUrl} className="bg-white" />

        <section id="contact" className="bg-slate-950 py-16 text-white sm:py-24" aria-labelledby="professional-contact">
          <div className="mx-auto grid max-w-6xl gap-10 px-5 sm:px-8 lg:grid-cols-[0.8fr_1.2fr]">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-300">Start a conversation</p>
              <h2 id="professional-contact" className="mt-3 text-4xl font-semibold tracking-tight">Tell {profile.firstName} what you’re planning.</h2>
              <p className="mt-5 max-w-md text-slate-400">Share a little context and your contact details. There is no obligation.</p>
              <div className="mt-8 flex items-start gap-3 text-sm text-slate-400">
                <Building2 className="mt-0.5 h-4 w-4" aria-hidden="true" />
                <span>{profile.companyInfo.name}{profile.companyInfo.address ? <><br />{profile.companyInfo.address}</> : null}</span>
              </div>
            </div>
            <PublicProfileLeadForm profile={profile} showLoanDetails />
          </div>
        </section>
      </main>

      <PublicProfileComplianceFooter profile={profile} tone="slate" />
    </div>
  );
}
